const normalizeText = (value) =>
  value === null || value === undefined ? '' : String(value).trim();

const isAvailableFlag = (value) =>
  !(value === false || value === 0 || value === '0' || value === 'false');

/**
 * Validate `single_product.variant_matrix`. Returns null when the payload is
 * missing or malformed so callers can fall back to the legacy option_data flow.
 */
export const normalizeVariantMatrix = (rawMatrix) => {
  if (!rawMatrix || typeof rawMatrix !== 'object') return null;
  if (!Array.isArray(rawMatrix.options) || !Array.isArray(rawMatrix.combinations)) {
    return null;
  }

  const options = rawMatrix.options.map((option) => ({
    name: normalizeText(option?.name),
    values: (Array.isArray(option?.values) ? option.values : [])
      .map(normalizeText)
      .filter(Boolean),
  }));
  if (
    options.length === 0 ||
    options.some((option) => !option.name || option.values.length === 0)
  ) {
    return null;
  }

  const combinations = rawMatrix.combinations
    .map((combination) => ({
      id: combination?.id ?? '',
      variant: normalizeText(combination?.variant),
      values: (Array.isArray(combination?.values) ? combination.values : []).map(
        normalizeText,
      ),
      available: isAvailableFlag(combination?.available),
    }))
    .filter(
      (combination) =>
        combination.variant &&
        combination.values.length === options.length &&
        combination.values.every(Boolean),
    );

  return combinations.length > 0 ? { options, combinations } : null;
};

// Same shape as Validation().varientTransformer so existing cart code keeps working.
export const matrixToVarientList = (matrix) =>
  (matrix?.options || []).map((option, index) => ({
    [`options${index + 1}`]: option.name,
    [`optionsvl${index + 1}`]: option.values,
  }));

const getSelectedEntries = (selected) =>
  Object.entries(selected && typeof selected === 'object' ? selected : {})
    .map(([slot, value]) => [Number(slot), normalizeText(value)])
    .filter(([slot, value]) => Number.isInteger(slot) && value);

// A value is enabled when some combination has it in `slot` and agrees with
// every other selected slot, so options can be picked in any order.
export const isValueEnabled = (matrix, selected, slot, value) => {
  if (!matrix) return true;
  const targetSlot = Number(slot);
  const targetValue = normalizeText(value);
  const entries = getSelectedEntries(selected);

  return matrix.combinations.some(
    (combination) =>
      combination.values[targetSlot] === targetValue &&
      entries.every(
        ([selectedSlot, selectedValue]) =>
          selectedSlot === targetSlot ||
          combination.values[selectedSlot] === selectedValue,
      ),
  );
};

// A value is clickable whenever some combination carries it in `slot`, even
// if it does not fit the other picks — selectionForPick() then moves them.
export const valueHasCombination = (matrix, slot, value) => {
  if (!matrix) return true;
  const targetSlot = Number(slot);
  const targetValue = normalizeText(value);
  return matrix.combinations.some(
    (combination) => combination.values[targetSlot] === targetValue,
  );
};

// Selection after clicking `value` in `slot`. The other picks stay while some
// combination still fits them; otherwise they jump to the first combination
// with this value (the first available one, so a click never lands on an
// out-of-stock variant when an in-stock one exists).
export const selectionForPick = (matrix, selected, slot, value) => {
  const next = {
    ...(selected && typeof selected === 'object' ? selected : {}),
    [slot]: value,
  };
  if (!matrix || isValueEnabled(matrix, selected, slot, value)) return next;
  const targetSlot = Number(slot);
  const targetValue = normalizeText(value);
  const withValue = matrix.combinations.filter(
    (combination) => combination.values[targetSlot] === targetValue,
  );
  const pick =
    withValue.find((combination) => combination.available) || withValue[0];
  return pick ? selectionFromCombination(pick) : next;
};

// Only resolves once every slot has a value.
export const findCombination = (matrix, selected) => {
  if (!matrix || !selected || typeof selected !== 'object') return null;
  const values = matrix.options.map((_, index) => normalizeText(selected[index]));
  if (values.some((value) => !value)) return null;

  return (
    matrix.combinations.find((combination) =>
      combination.values.every((value, index) => value === values[index]),
    ) || null
  );
};

export const findCombinationByVariant = (matrix, variant) => {
  const key = normalizeText(variant);
  if (!matrix || !key) return null;
  return (
    matrix.combinations.find((combination) => combination.variant === key) ||
    null
  );
};

export const selectionFromCombination = (combination) =>
  (combination?.values || []).reduce((selection, value, index) => {
    selection[index] = value;
    return selection;
  }, {});

// Preferred key first (when available), then the first available combination.
export const getInitialCombination = (matrix, preferredVariant) => {
  if (!matrix) return null;
  const preferred = findCombinationByVariant(matrix, preferredVariant);
  if (preferred?.available) return preferred;
  return (
    matrix.combinations.find((combination) => combination.available) ||
    preferred ||
    matrix.combinations[0]
  );
};

// Display only: never use the label for selection, cart matching or orders.
export const findVariantLabel = (rows, { variant, id } = {}) => {
  if (!Array.isArray(rows)) return '';
  const key = normalizeText(variant);
  const normalizedId = normalizeText(id);
  const row =
    (key && rows.find((item) => normalizeText(item?.variant) === key)) ||
    (normalizedId &&
      rows.find((item) => normalizeText(item?.id) === normalizedId));
  return normalizeText(row?.variant_label);
};

export const getVariantLabelRows = (productData) => [
  ...(Array.isArray(productData?.in_stock_variants)
    ? productData.in_stock_variants
    : []),
  ...(Array.isArray(productData?.zero_quantity_variants)
    ? productData.zero_quantity_variants
    : []),
];

// Cart items saved from a variant_matrix carry the API's exact `variant`
// string. Returns '' for older items so callers keep their existing join.
export const getStoredCartVariant = (item) => normalizeText(item?.variant);
